import {IPixiAppService} from "../../services/pixi-app/pixi-app.service.interface";
import {PixiAnimation} from "../../services/pixi-app/pixi-animation"
import {IGsapService} from "../../services/gsap/gsap.service.interface";
import {DESIGN_DIMENSIONS} from "./magic-8-ball-consts";
import {IMagic8BallService} from "../../services/magic-8-ball/magic-8-ball.service.interface";
import {IThemeService} from "../../services/theme/theme.service.interface";
import {ITranslationService} from "../../services/translation/translation.service.interface";
import {IMediaQueryService} from "../../services/media-query/media-queries.interface";
import {IInGameTicketService} from "../../services/in-game-ticket/in-game-ticket.service.interface";
import {CyClass} from "../../../common/cy-classes";
import {IInGameTicketPrize} from "../../services/in-game-ticket/prize/abstract/in-game-ticket-prize-types";
import {Magic8BallAnimationRenderer} from "./magic-8-ball-animation-renderer";
import {InGameTicketModel} from "../../services/in-game-ticket/in-game-ticket.model";
import {IInGameChallengeService} from "../../services/in-game-challenge/in-game-challenge.service.interface";
import {MAGIC8BALL_OPENED_FROM} from "../../services/magic-8-ball/magic-8-ball-opened-from.enum";

export class Magic8BallAnimation {
    private ticket: null | InGameTicketModel = null;
    private pixiAnimation: PixiAnimation | null = null;

    constructor(
        private services: {
            pixiApp: IPixiAppService;
            gsap: IGsapService;
            magic8ball: IMagic8BallService;
            theme: IThemeService;
            translation: ITranslationService;
            mediaQuery: IMediaQueryService;
            inGameTicket: IInGameTicketService;
            inGameChallenge: IInGameChallengeService;
        },
        private openedFrom: MAGIC8BALL_OPENED_FROM,
        private onResize: undefined | ((scale: number) => void),
    ) {
    }
    async init() {
        this.ticket = this.services.inGameTicket.getTicket();
        
        const prizesPromise = this.ticket ? this.ticket.getPrizes().then((prizesResponse) => {
            return prizesResponse.prizes;
        }).catch((e) => {
            this.ticket.resetGetPrizesFailedPromise();
            throw e;
        }) : Promise.resolve(undefined);

        // ATTENTION: prizes is used only for the first set of rizes when the mini game opens. It does not update when the ticket updates
        const prizes: IInGameTicketPrize[] | undefined = await prizesPromise;

        this.pixiAnimation = this.services.pixiApp.createPixiAnimation(
            {
                width: DESIGN_DIMENSIONS.WIDTH,
                height: DESIGN_DIMENSIONS.HEIGHT,
                backgroundColor: "0xcccccc"
            },
            {
                'backgroundParticles': this.services.theme.currentTheme.assets.images.magic_8_ball.particles.backgroundParticles_json.toString(),
                // Not used for the moment but might change
                // 'ellipse': this.services.theme.currentTheme.assets.images.magic_8_ball.ellipse_png.toString(),
                'star': this.services.theme.currentTheme.assets.images.magic_8_ball.star_png.toString(),
                'ball': this.services.theme.currentTheme.assets.images.magic_8_ball.spine._8_ball_json.toString(),
                'background': this.services.theme.currentTheme.assets.images.magic_8_ball.spine.bg_json.toString(),
                'blue_layer': this.services.theme.currentTheme.assets.images.magic_8_ball.blue_layer_png.toString(),
                'button': this.services.theme.currentTheme.assets.images.magic_8_ball.spine.button_json.toString(),
                'prize': this.services.theme.currentTheme.assets.images.magic_8_ball.prize_png.toString(),
                'transparentTextureBg': this.services.theme.currentTheme.assets.images.magic_8_ball.text_bg_transparent_png.toString()
                /**
                 * In case we would use a font in the animation that is not used in the Orbit app then we would need to load the font
                 * Like this: "888-Regular": "./fonts/888-Regular.woff"
                 * Beware, if using a font name that starts with a number like in the example above it won't work. The font filename should start with a letter
                 * (I found this by experimenting, haven't found information written anywhere)
                 * So I would recommend instead of renaming the file "888-Regular" to "Triple8-Regular" for example.
                 * Also, if the filename has "-" inside of it, the naming of the fontFamily used when creating text must contain spaces instead of dashes
                 * Example if the filename is "Something-Otherthing-Regular" the fontFamily would be "Something Otherthing Regular"
                 */
            },
            {
                render: ({pixiApp, resources: assets, addDisposeAction, pixiLibWrapper}) => {

                    const magic8BallRenderer = new Magic8BallAnimationRenderer(
                        this.services,
                        pixiApp,
                        assets,
                        addDisposeAction,
                        pixiLibWrapper,
                        // ATTENTION: prizes is used only for the first set of rizes when the mini game opens. It does not update when the ticket updates
                        prizes,
                        this.openedFrom,
                        () => {
                            if (this.ticket.isExpired) {
                                this.services.inGameTicket.removeExpiredTickets();
                                this.services.magic8ball.onError(this.services.translation.translate("MAGIC_8_BALL_CLAIM_PRIZE_TICKET_EXPIRED_ERROR"));
                                return Promise.resolve(-1);
                            }
                            // at this point getPrizes promise must be cached and fulfilled because otherwise we would not get to the point where we can shake the ball
                            // ATTENTION: do no use prizes. prizes is used only for the first set of rizes when the mini game opens. It does not update when the ticket updates
                            const claimPromise = Promise.all([this.ticket.claimTicket(), this.ticket.getPrizes()]).then(([actualPrizeResult, prizesResult]) => {
                                const actualPrize = actualPrizeResult.prize;
                                const actualPrizeIndex = prizesResult.prizes.findIndex((possiblePrize) => {
                                    return actualPrize.isEqual(possiblePrize)
                                });
                                if (actualPrizeIndex < 0) {
                                    throw new Error("Prize: " + actualPrize + " is not in the list of possible prizes");
                                }
                                return actualPrizeIndex;
                            });
                            claimPromise.then(() => {
                                this.services.inGameTicket.removeClaimedTickets();
                                // the claimed ticket was removed from available tickets so getTicket gives us the next one
                                // we just want to trigger the prizes api call that will get cached, we do nothing else for now
                                const nextTicket = this.services.inGameTicket.getTicket();
                                // of course, if there are no tickets left nextTicket is null and we don't need to call getPrizes
                                if (nextTicket) {
                                    nextTicket.getPrizes();
                                }
                            })
                            return claimPromise.catch((e) => {
                                this.services.magic8ball.onError(this.services.translation.translate("MAGIC_8_BALL_CLAIM_PRIZE_ERROR"), e);
                                return Promise.resolve(-1);
                            });
                        },
                        () => {
                            // if there are more tickets, this.ticket will not be null and we ned to show the prizes for the next shake
                            this.ticket = this.services.inGameTicket.getTicket();
                            if (!this.ticket) {
                                return Promise.resolve(null);
                            }
                            return this.ticket.getPrizes().then(prizesResponse => {
                                return prizesResponse.prizes;
                            }).catch(e => {
                                this.ticket.resetGetPrizesFailedPromise();
                                this.services.magic8ball.onError(this.services.translation.translate("MAGIC_8_BALL_GET_PRIZES_ERROR"), e);
                                return null;
                            });
                        },
                        () => {
                            return this.ticket === null
                        },
                        this.onResize
                    );

                    return () => {
                        magic8BallRenderer.dispose();
                    };
                },
                dispose: () => {}
            },
            undefined, // canvasCss?: CSSProperties
            CyClass('cy-magic8ball-minigame-canvas'),
            true // debugMode: boolean
        );

        return await this.pixiAnimation.start();
    }

    attachToDOM(wrapperElement: HTMLElement) {
        this.pixiAnimation?.attachToDOM(wrapperElement);
    }
    dispose() {
        this.pixiAnimation?.dispose();
    }

}
